import BaseNode from "~/components/Playground/Node/BaseNode";
import * as HoverCard from "~/components/Playground/Node/StepHoverCard";
import {
  BlockStep,
  InputStep,
  Outcome,
  State,
  hasReceivedInput,
  isInputDisabled,
} from "~/lib/pipeline";
import { Node, NodeProps } from "@xyflow/react";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { forwardRef, memo, useEffect, useState } from "react";
import { isInputJobUnblocked, InputJob } from "~/components/build/Show/lib/types";
import classNames from "classnames";
import Icon from "~/components/shared/Icon";
import UserAvatar from "~/components/shared/UserAvatar";
import FriendlyTime from "~/components/shared/FriendlyTime";
import { twMerge } from "tailwind-merge";
import Emojify from "~/components/shared/Emojify";
import { HoverCardContentProps } from "@radix-ui/react-hover-card";
import { StateIcon } from "~/components/Playground/Node/BaseNode/StateIcon";
import { fetchBuildStepJobs } from "~/components/build/Show/components/Sidebar/TreeNode/fetchBuildStepJobs";
import { isInputNodeDisabled } from "~/components/build/Show/lib/types/InputJob";
import { useStepDependencies } from "~/components/build/Show/lib/useStepDependencies";
import { useQueryClient } from "@tanstack/react-query";

interface InputStepHoverCardProps extends HoverCardContentProps {
  step: InputStep | BlockStep;
}

/**
 * Find the first job associated with the given step.
 *
 * If we make the unblock dialog render with a step, we can remove all of this.
 */
const useInputJob = (step: InputStep | BlockStep): InputJob | null => {
  const { build } = useBuild({ strict: false });
  const queryClient = useQueryClient();
  const [job, setJob] = useState<InputJob | null>(null);

  useEffect(() => {
    if (build) {
      fetchBuildStepJobs<InputJob>(queryClient, step, build).then((jobs) => {
        setJob(jobs[0] || null);
      });
    }
  }, [step, build]);

  if (!build) {
    return null;
  }

  return job;
};

export const InputStepHoverCard = forwardRef<HTMLDivElement, InputStepHoverCardProps>(
  ({ step, ...props }, ref) => {
    const job = useInputJob(step);
    const { missingDependencies } = useStepDependencies(step.uuid);

    if (!job || !isInputJobUnblocked(job)) {
      return null;
    }

    return (
      <HoverCard.Content
        ref={ref}
        state={step.state}
        outcome={step.outcome}
        missingDependencies={missingDependencies}
        {...props}
      >
        <div className="space-y-3">
          <div className="flex w-full flex-row items-center gap-2">
            <UserAvatar
              className="h-10 w-10"
              user={{
                name: job.unblockedBy.name,
                avatar: { url: job.unblockedBy.avatarUrl },
              }}
            />
            <div className="flex flex-1 flex-col flex-nowrap">
              <div className="font-medium">{job.unblockedBy.name}</div>
              <div className="dark-gray">
                Unblocked <FriendlyTime value={job.unblockedAt} capitalized={false} />
              </div>
            </div>
          </div>
        </div>
      </HoverCard.Content>
    );
  },
);

interface UnblockButtonProps {
  step: InputStep | BlockStep;
  children?: React.ReactNode;
  className?: string;
}

export const UnblockButton = forwardRef<HTMLAnchorElement, UnblockButtonProps>(
  ({ step, children, className, ...props }, ref) => {
    const job = useInputJob(step);

    return (
      <a
        {...props}
        ref={ref}
        href={job?.unblockDialogPath}
        aria-disabled={job && isInputNodeDisabled(job) ? true : undefined}
        data-turbo={true}
        data-turbo-frame="unblock_dialog"
        className={twMerge(
          "text-inherit no-underline hover:text-inherit focus:text-inherit",
          className,
        )}
      >
        {children}
      </a>
    );
  },
);

type InputStepNode = Node<InputStep, "input">;

export const InputStepUser = ({ step }: { step: InputStep | BlockStep }) => {
  const job = useInputJob(step);

  if (Features.ProgressiveBuildShow) {
    // todo: get user passing into step.
    return null;
  }

  return (
    <div className="flex size-6 items-center justify-center">
      {job?.unblockedBy && (
        <UserAvatar
          user={{
            name: job?.unblockedBy?.name,
            avatar: {
              url: job?.unblockedBy?.avatarUrl || "",
            },
          }}
        />
      )}
    </div>
  );
};

function InputStepNode(props: NodeProps<InputStepNode>) {
  const { missingDependencies } = useStepDependencies(props.id);

  const node = (
    <div>
      <BaseNode
        {...props}
        label={<Emojify text={props.data.label || "Continue"} />}
        icon={
          hasReceivedInput(props.data) ? (
            <>
              <InputStepUser step={props.data} />

              <div className="flex shrink-0 items-center justify-center self-stretch">
                <StateIcon
                  state={State.Finished}
                  outcome={Outcome.Passed}
                  missingDependencies={missingDependencies}
                />
              </div>
            </>
          ) : (
            <span
              className={twMerge(
                classNames(
                  "absolute -bottom-[1px] -right-[1px] -top-[1px] flex w-10 items-center justify-center rounded-r-lg bg-purple-600",
                  {
                    "bg-slate-500": isInputDisabled(props.data),
                  },
                ),
              )}
            >
              <Icon icon="arrow-right" className="h-5 w-5 text-white" />
            </span>
          )
        }
        disabled={isInputDisabled(props.data)}
        className={classNames({
          "opacity-100": props.data.state !== State.Ignored,
          "border-4 border-purple-600 pr-10":
            !hasReceivedInput(props.data) && !isInputDisabled(props.data),
          "border-2 pr-10 text-slate-500": isInputDisabled(props.data),
        })}
      />
    </div>
  );

  return (
    <HoverCard.Root>
      <HoverCard.Portal container={document.getElementById("canvas")}>
        <InputStepHoverCard
          step={props.data}
          collisionBoundary={document.getElementById("canvas")}
          collisionPadding={10}
        />
      </HoverCard.Portal>

      <HoverCard.Trigger asChild={true}>
        {hasReceivedInput(props.data) || isInputDisabled(props.data) ? (
          node
        ) : (
          <UnblockButton step={props.data}>{node}</UnblockButton>
        )}
      </HoverCard.Trigger>
    </HoverCard.Root>
  );
}

export default memo(InputStepNode);
